import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  AlertTitle
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const ExistingUserDialog = ({ open, onClose, existingUser, conflictType }) => {
  const navigate = useNavigate();

  if (!existingUser) return null;

  const handleViewUser = () => {
    navigate(`/customers/${existingUser.id}`);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getConflictMessage = () => {
    if (conflictType === 'backup_phone_number') {
      return 'Số điện thoại dự phòng này đã được sử dụng bởi khách hàng khác trong hệ thống.';
    }
    return 'Số điện thoại này đã được sử dụng bởi khách hàng khác trong hệ thống.';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="warning" />
          <Typography variant="h6" component="span">
            Khách hàng đã tồn tại
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Cảnh báo trùng lặp</AlertTitle>
          {getConflictMessage()}
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Thông tin khách hàng hiện có:
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Name */}
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                Tên:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {existingUser.name}
              </Typography>
            </Box>

            <Divider />

            {/* Phone Number */}
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                Số ĐT:
              </Typography>
              <Typography variant="body1">
                {existingUser.phone_number || 'N/A'}
              </Typography>
            </Box>

            {/* Backup Phone Number */}
            {existingUser.backup_phone_number && (
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  SĐT dự phòng:
                </Typography>
                <Typography variant="body1">
                  {existingUser.backup_phone_number}
                </Typography>
              </Box>
            )}

            <Divider />

            {/* Email */}
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                Email:
              </Typography>
              <Typography variant="body1">
                {existingUser.email || 'N/A'}
              </Typography>
            </Box>

            <Divider />

            {/* Tags */}
            {existingUser.tags && (
              <>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocalOfferIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                    Tags:
                  </Typography>
                  <Typography variant="body1">
                    {existingUser.tags}
                  </Typography>
                </Box>
                <Divider />
              </>
            )}

            {/* Created Date */}
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarTodayIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                Ngày tạo:
              </Typography>
              <Typography variant="body1">
                {formatDate(existingUser.created_at)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          Vui lòng kiểm tra thông tin khách hàng hiện có. Nếu đây là cùng một khách hàng,
          bạn có thể chỉnh sửa thông tin của họ thay vì tạo mới.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Đóng
        </Button>
        <Button
          onClick={handleViewUser}
          variant="contained"
          color="primary"
        >
          Xem thông tin khách hàng
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExistingUserDialog;
